import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { arrayUnion, doc, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

export const UserContext = createContext();

const normalizeUserProfile = (profile = {}, uid = profile.uid) => ({
  ...profile,
  uid,
  isFederal: profile.isFederal === true,
});

const normalizeEmailValue = (value) =>
  String(value || '').trim().toLowerCase();

export function UserProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [profileExists, setProfileExists] = useState(null);
  const [loading, setLoading] = useState(true);
  const emailSyncRef = useRef(null);

  useEffect(() => {
    if (currentUser) {
      localStorage.setItem(
        'currentUserProfile',
        JSON.stringify(currentUser)
      );
    } else {
      localStorage.removeItem('currentUserProfile');
    }
  }, [currentUser]);

  useEffect(() => {
    const auth = getAuth();
    let unsubscribeDoc = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (unsubscribeDoc) {
        unsubscribeDoc();
        unsubscribeDoc = null;
      }

      if (user) {
        const docRef = doc(db, 'users', user.uid);

        // subscribe to changes in Firestore user doc
        unsubscribeDoc = onSnapshot(docRef, async (docSnap) => {
          if (docSnap.exists()) {
            const profileData = docSnap.data();
            const authEmail = normalizeEmailValue(user.email);
            const firestoreEmail = normalizeEmailValue(profileData.email);
            const pending = profileData.pendingEmailChange || null;
            const pendingMatchesAuth =
              pending?.status === 'pending' &&
              pending?.uid === user.uid &&
              normalizeEmailValue(pending?.newEmail) === authEmail;
            const needsEmailFieldSync =
              !!authEmail && authEmail !== firestoreEmail;
            const needsPendingCompletion = pendingMatchesAuth;

            if (
              (needsEmailFieldSync || needsPendingCompletion) &&
              emailSyncRef.current !== user.uid
            ) {
              emailSyncRef.current = user.uid;

              const updatePayload = {};
              if (needsPendingCompletion) {
                if (pending?.status === 'completed') {
                  emailSyncRef.current = null;
                  setCurrentUser(normalizeUserProfile(profileData, user.uid));
                  setProfileExists(true);
                  setLoading(false);
                  return;
                }

                updatePayload.email = user.email || '';
                updatePayload.pendingEmailChange = {
                  ...pending,
                  status: 'completed',
                  completedAt: serverTimestamp(),
                };
                updatePayload.verificationStatus = null;
                updatePayload.reverificationReason = 'email_change';
                updatePayload.verified = false;
                updatePayload.changesLog = arrayUnion({
                  field: 'email',
                  oldValue: profileData.email || 'N/A',
                  newValue: user.email || 'N/A',
                  changedAt: new Date().toISOString(),
                  reason: 'user_update',
                });
              } else if (needsEmailFieldSync) {
                updatePayload.email = user.email || '';
                if (firestoreEmail) {
                  updatePayload.changesLog = arrayUnion({
                    field: 'email',
                    oldValue: profileData.email || 'N/A',
                    newValue: user.email || 'N/A',
                    changedAt: new Date().toISOString(),
                    reason: 'system_recovery',
                  });
                }
              }

              try {
                await updateDoc(docRef, updatePayload);
              } catch (err) {
                console.error('Failed to sync email from Auth to Firestore:', err);
              } finally {
                emailSyncRef.current = null;
              }
            }

            setCurrentUser(normalizeUserProfile(profileData, user.uid));
            setProfileExists(true);
          } else {
            setCurrentUser(normalizeUserProfile({}, user.uid));
            setProfileExists(false);
          }
          setLoading(false);
        });
      } else {
        setCurrentUser(null);
        setProfileExists(null);
        sessionStorage.removeItem('sessionAcknowledged');
        emailSyncRef.current = null;
        setLoading(false);
      }
    });

    return () => {
      if (unsubscribeDoc) {
        unsubscribeDoc();
      }
      unsubscribeAuth();
    };
  }, []);

  return (
    <UserContext.Provider value={{
      currentUser,
      setCurrentUser,
      profileExists,
      loading,
    }}>
      {!loading && children}
    </UserContext.Provider>
  );
}

export const useUser = () => useContext(UserContext);
